import ProfileImage from 'pages/OraclePayment/components/ProfileImage';
import { useTranslation } from 'react-i18next';
import {
  BillWrapper,
  MerchantRow,
  MerchantText,
  MerchantBrand,
  HeroAmountSection,
  HeroAmountLabel,
  BillContent,
  MerchantName,
  MerchantSubline,
  Item,
  Name,
  Price,
  LargePrice,
  Field,
  Invoice,
  HorizontalLine,
  DetailEmptyMenuItem,
  SubTotalContainer,
  PaymentMeta,
  PaymentMetaRow,
  PaymentMetaLabel,
  PaymentMetaValue,
  PreAuthNotice,
} from './index.view';
import { currencyFormat } from 'utils/number';
import { Merchant } from 'utils/types/oraclePayment/Merchant';
import MenuList from './components/MenuList';
import Tip from './components/Tip';
import Tax from './components/Tax';
import ExtraFee from './components/ExtraFee';
import useViewModel from './viewModel';
import { Payload } from 'baseTypes';
import DiscountDetail from './components/Discount/DiscountDetail';
import Voucher from './components/Voucher';
import { isEmpty } from 'lodash-es';
import { usePlacingPayment } from 'contexts/placingPayment';
import SplitBill from 'components/Bill/components/SplitBill';

interface BillProps {
  merchant: Merchant;
  menuItems: Payload;
  total: number;
  totalTax: number;
  invoiceNumber?: string;
  type: string;
  fullAmount: number;
  extraFee: { name: string; price: number }[];
}

const Bill = ({
  merchant,
  menuItems: menuItemsProp,
  total,
  totalTax,
  invoiceNumber,
  type,
  fullAmount,
  extraFee,
}: BillProps) => {
  const { t } = useTranslation();
  const {
    totalBill,
    totalTipPercent,
    menuItems,
    discount,
    totalAmountExcludedFee,
    voucherCode,
    voucherError,
    voucherLoading,
    voucherDiscount,
    voucherCoversFullAmount,
    applyVoucher,
    clearVoucher,
    payWithVoucher,
  } = useViewModel({
    total,
    fullAmount,
    menuItems: menuItemsProp,
    type,
  });

  const { isSplitBill, paymentInfo } = usePlacingPayment();
  const paymentReference = paymentInfo.refKey || paymentInfo.orderId || paymentInfo.pid;
  const isPreAuth =
    paymentInfo?.preAuth === true ||
    paymentInfo?.pre_auth === true ||
    String(paymentInfo?.status || '').toLowerCase() === 'authorized';

  return (
    <BillWrapper>
      <MerchantRow>
        <MerchantBrand>
          <ProfileImage
            width={92}
            height={64}
            borderSize={0}
            source={merchant.logoImage}
            rounded={false}
            objectFit="contain"
          />
        </MerchantBrand>
        <MerchantText>
          <MerchantName>{merchant.companyName}</MerchantName>
        </MerchantText>
      </MerchantRow>
      <HeroAmountSection>
        <HeroAmountLabel>{t('oracleTotalBill')}</HeroAmountLabel>
        <LargePrice>{currencyFormat(totalBill)}</LargePrice>
      </HeroAmountSection>
      <PaymentMeta>
        {paymentReference ? (
          <PaymentMetaRow>
            <PaymentMetaLabel>Referenz</PaymentMetaLabel>
            <PaymentMetaValue>{paymentReference}</PaymentMetaValue>
          </PaymentMetaRow>
        ) : null}
      </PaymentMeta>
      {isPreAuth && (
        <PreAuthNotice>
          <strong>Reservierung</strong> – Der Betrag wird nur reserviert und erst nach Bestätigung durch den Anbieter abgebucht.
        </PreAuthNotice>
      )}
      <BillContent>
        <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
          {menuItems.length > 0 && (
            <>
              {invoiceNumber && (
                <Invoice>
                  <span>{t('invoiceNumber')}</span>
                  <span>{invoiceNumber}</span>
                </Invoice>
              )}
              <HorizontalLine size={1} />
              <MenuList menuItems={menuItems} />
            </>
          )}
          <HorizontalLine size={1} />
          {menuItems.length > 0 ? (
            totalTipPercent === 0 && isEmpty(discount) ? (
              <>
                <SubTotalContainer>
                  {totalTipPercent !== 0 || !isEmpty(discount) || extraFee ? (
                    <Item>
                      <Field>{t('subtotal')}</Field>
                      <Field>{currencyFormat(totalAmountExcludedFee())}</Field>
                    </Item>
                  ) : null}
                  <Tax amount={totalTax} />
                </SubTotalContainer>
                <ExtraFee extraFee={extraFee} />
                <Item>
                  <Name bold>{t('oracleTotalBill')}</Name>
                  <Price bold>{currencyFormat(totalBill)}</Price>
                </Item>
                {isSplitBill && (
                  <Item>
                    <SplitBill
                      splitAmount={
                        paymentInfo.placingPayment.totalSplitBill ??
                        paymentInfo.payload.checkoutItems.length
                      }
                      type={type}
                    />
                  </Item>
                )}
              </>
            ) : (
              <>
                <SubTotalContainer>
                  {totalTipPercent !== 0 || !isEmpty(discount) || extraFee ? (
                    <Item>
                      <Field>{t('subtotal')}</Field>
                      <Field>{currencyFormat(totalAmountExcludedFee())}</Field>
                    </Item>
                  ) : null}
                  <Tax amount={totalTax} />
                </SubTotalContainer>
                <DiscountDetail discount={discount} />
                <ExtraFee extraFee={extraFee} includedTax={paymentInfo?.payload?.checkoutItems} />
                <Tip amount={totalAmountExcludedFee()} />
                <Item>
                  <Name bold>{t('oracleTotalBill')}</Name>
                  <Price bold>{currencyFormat(totalBill)}</Price>
                </Item>
                {isSplitBill && (
                  <Item>
                    <SplitBill
                      splitAmount={
                        paymentInfo.placingPayment.totalSplitBill ??
                        paymentInfo.payload.checkoutItems.length
                      }
                      type={type}
                    />
                  </Item>
                )}
              </>
            )
          ) : (
            <DetailEmptyMenuItem>
              <Item style={{ flexDirection: 'column', alignItems: 'center' }}>
                <Name bold>Details</Name>
              </Item>
              {totalTipPercent !== 0 || !isEmpty(discount) || isSplitBill ? (
                <>
                  <Item>
                    <Field>{t('subtotal')}</Field>
                    <Field>{currencyFormat(totalAmountExcludedFee())}</Field>
                  </Item>
                  {isSplitBill ? (
                    <Item>
                      <SplitBill
                        splitAmount={
                          paymentInfo.placingPayment.totalSplitBill ??
                          paymentInfo.payload.checkoutItems.length
                        }
                        type={type}
                      />
                    </Item>
                  ) : null}
                </>
              ) : null}
              {!isEmpty(discount) && !isSplitBill ? <DiscountDetail discount={discount} /> : null}
              <Tip amount={totalAmountExcludedFee()} />
            </DetailEmptyMenuItem>
          )}
        </div>
      </BillContent>
      {!isSplitBill && (
        <Voucher
          voucherCode={voucherCode}
          applyVoucher={applyVoucher}
          clearVoucher={clearVoucher}
          payWithVoucher={payWithVoucher}
          error={voucherError}
          loading={voucherLoading}
          voucherDiscount={voucherDiscount}
          coversFullAmount={voucherCoversFullAmount}
        />
      )}
    </BillWrapper>
  );
};

export default Bill;
